import React, { useState } from 'react';
import * as ReactRouterDom from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { Upload, CheckCircle2, Loader2, Activity, Download } from 'lucide-react';
import Papa, { ParseResult } from 'papaparse';
import { Button } from '../../components/ui/Button';
import { normalizeOrg } from '../../lib/utils';

const { useNavigate } = ReactRouterDom as any;

type WizardStep = 'upload' | 'mapping' | 'reconciliation' | 'importing' | 'complete';
interface CSVRow { [key: string]: string; }
interface ReconciledRow {
  csvRow: CSVRow;
  matchType: 'new' | 'exact';
  existingId: string | null;
}

const INITIAL_MAPPING = {
  organization: '',
  org_type: '',
  website: '',
  street_address: '',
  city: '',
  state: '',
  zip_code: '',
  notes: ''
};

export const OrgImportWizard: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStep>('upload');
  const [parsedData, setParsedData] = useState<CSVRow[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>(INITIAL_MAPPING);
  const [reconciliation, setReconciliation] = useState<{ new: number; matched: number; rows: ReconciledRow[] }>({ new: 0, matched: 0, rows: [] });
  const [importResults, setImportResults] = useState({ success: 0, updated: 0, errors: 0 });
  const [isProcessing, setIsProcessing] = useState(false);

  const { data: orgTypes } = useQuery({
    queryKey: ['org-types-import-sync'],
    queryFn: async () => (await supabase.from('organization_types').select('*')).data || []
  });

  const downloadTemplate = () => {
    const csvContent = "Organization,Org Type,Website,Street Address,City,State,Zip Code,Notes\nExample Institution,Partner,https://example.org,123 Main St,Detroit,MI,48201,Legacy Partner";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'organizations_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      Papa.parse(e.target.files[0], {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (results: ParseResult<CSVRow>) => {
          const headers = results.meta.fields || (results.data[0] ? Object.keys(results.data[0]) : []);
          setMapping(prev => {
            const next = { ...prev };
            headers.forEach(h => {
              const l = h.toLowerCase().trim();
              if(l.includes('org') || l.includes('name')) next.organization = h;
              if(l.includes('type')) next.org_type = h;
              if(l.includes('web')) next.website = h;
              if(l.includes('address')) next.street_address = h;
              if(l.includes('city')) next.city = h;
              if(l.includes('state')) next.state = h;
              if(l.includes('zip')) next.zip_code = h;
            });
            return next;
          });
          setParsedData(results.data);
          setStep('mapping');
        }
      });
    }
  };

  const startReconciliation = async () => {
    setIsProcessing(true);
    const names = Array.from(new Set(parsedData.map(r => r[mapping.organization]?.trim()).filter(Boolean)));
    const { data: existingOrgs } = await supabase.from('organizations').select('id, name').in('name', names);
    const orgMap = new Map<string, string>(existingOrgs?.map(o => [normalizeOrg(o.name), o.id]) || []);

    const rows: ReconciledRow[] = parsedData.map(row => {
      const name = row[mapping.organization]?.trim();
      const existingId = name ? orgMap.get(normalizeOrg(name)) : null;
      return { csvRow: row, matchType: existingId ? 'exact' : 'new', existingId: (existingId as string) || null };
    });

    setReconciliation({ new: rows.filter(r => r.matchType === 'new').length, matched: rows.filter(r => r.matchType === 'exact').length, rows });
    setIsProcessing(false);
    setStep('reconciliation');
  };

  const runImport = async () => {
    setIsProcessing(true);
    setStep('importing');
    let success = 0, updated = 0, errors = 0;

    for (const item of reconciliation.rows) {
      try {
        const typeLabel = item.csvRow[mapping.org_type]?.trim();
        let typeId = orgTypes?.find(t => t.label.toLowerCase() === typeLabel?.toLowerCase())?.id;
        if (!typeId) typeId = orgTypes?.find(t => t.label.toLowerCase() === 'general')?.id || orgTypes?.[0]?.id;
        
        const payload = {
          name: item.csvRow[mapping.organization]?.trim(),
          org_type_id: typeId || null,
          website: item.csvRow[mapping.website]?.trim() || null,
          street_address: item.csvRow[mapping.street_address]?.trim() || null,
          city: item.csvRow[mapping.city]?.trim() || null,
          state: item.csvRow[mapping.state]?.trim() || null,
          zip_code: item.csvRow[mapping.zip_code]?.trim() || null,
          notes: item.csvRow[mapping.notes]?.trim() || null,
          created_by_email: profile?.email
        };

        if (item.matchType === 'exact' && item.existingId) {
          await supabase.from('organizations').update(payload).eq('id', item.existingId);
          updated++;
        } else {
          await supabase.from('organizations').insert([payload]);
          success++;
        }
      } catch (err) { errors++; }
    }
    setImportResults({ success, updated, errors });
    setStep('complete');
    setIsProcessing(false);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-24">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase">Institutional Registry Sync</h1>
          <p className="text-slate-500 font-bold text-[10px] uppercase tracking-widest mt-1">Bulk Organization Onboarding</p>
        </div>
        <button onClick={() => navigate('/organizations')} className="text-[10px] font-black uppercase text-slate-400 hover:text-slate-900 tracking-widest">ABORT</button>
      </div>

      <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden min-h-[500px] flex flex-col">
        {step === 'upload' && (
          <div className="flex-1 p-20 flex flex-col items-center justify-center text-center">
            <Upload className="text-slate-100 mb-6" size={64} />
            <h3 className="text-xl font-black uppercase">Load Institutional CSV</h3>
            <div className="flex flex-col items-center gap-4 mt-8">
              <label className="cursor-pointer px-12 py-5 bg-slate-900 text-[#FFDB00] rounded font-black uppercase text-[10px] tracking-widest hover:bg-black transition-all shadow-xl">
                Select Dataset
                <input type="file" className="hidden" accept=".csv" onChange={handleFileChange} />
              </label>
              <button onClick={downloadTemplate} className="text-slate-500 hover:text-slate-900 text-[10px] font-black uppercase tracking-widest">
                <Download size={14} className="inline mr-2" /> Download Template
              </button>
            </div>
          </div>
        )}

        {step === 'mapping' && (
          <div className="p-10 space-y-8">
             <div className="grid grid-cols-1 gap-2">
               {Object.keys(INITIAL_MAPPING).map(field => (
                  <div key={field} className="flex items-center justify-between p-4 bg-slate-50 rounded border border-slate-100">
                    <span className="font-black text-[11px] uppercase">{field.replace('_', ' ')}</span>
                    <select value={mapping[field]} onChange={(e) => setMapping(prev => ({ ...prev, [field]: e.target.value }))} className="bg-white border border-slate-200 rounded px-4 py-2 text-[11px] font-bold w-64 shadow-inner">
                      <option value="">-- Unmapped --</option>
                      {Object.keys(parsedData[0] || {}).map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
               ))}
             </div>
             <div className="flex justify-between items-center pt-8 border-t border-slate-50">
               <Button variant="ghost" onClick={() => setStep('upload')}>Back</Button>
               <Button onClick={startReconciliation} disabled={!mapping.organization} isLoading={isProcessing}>Analyze Registry</Button>
             </div>
          </div>
        )}

        {step === 'reconciliation' && (
          <div className="p-10 space-y-8">
             <div className="overflow-x-auto border border-slate-200 rounded-md">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-900 text-[#FFDB00] text-[9px] font-black uppercase tracking-widest">
                    <tr><th className="px-6 py-4">Institution</th><th className="px-6 py-4">Type Match</th><th className="px-6 py-4 text-right">Impact</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {reconciliation.rows.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50/50">
                        <td className="px-6 py-4">
                          <p className="font-black uppercase text-slate-900 text-[11px] truncate">{r.csvRow[mapping.organization]}</p>
                        </td>
                        <td className="px-6 py-4">
                           <span className="text-[10px] font-bold text-slate-500 uppercase">{r.csvRow[mapping.org_type] || 'General'}</span>
                        </td>
                        <td className="px-6 py-4 text-right">
                           <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded border ${r.matchType === 'new' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                             {r.matchType === 'new' ? 'Registry Entry' : 'Profile Refresh'}
                           </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
             </div>
             <div className="flex gap-4 pt-6 border-t border-slate-50">
                <Button variant="outline" onClick={() => setStep('mapping')} className="flex-1">Back</Button>
                <Button onClick={runImport} className="flex-[2] py-4 shadow-xl" icon={<Activity size={18} />}>Commit To Registry</Button>
             </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="flex-1 p-20 text-center flex flex-col items-center justify-center">
            <Loader2 className="animate-spin text-slate-900 mb-6" size={64} />
            <h3 className="text-xl font-black uppercase text-slate-900">Synchronizing Institutions</h3>
          </div>
        )}

        {step === 'complete' && (
          <div className="flex-1 p-20 text-center flex flex-col items-center justify-center animate-in zoom-in-95">
            <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mb-8 shadow-inner"><CheckCircle2 size={40} /></div>
            <h3 className="text-3xl font-black text-slate-900 uppercase mb-8">Sync Complete</h3>
            <div className="grid grid-cols-2 gap-4 w-full max-w-lg mb-12">
              <div className="p-8 bg-slate-50 rounded border border-slate-100 text-center"><p className="text-4xl font-black">{importResults.success}</p><p className="text-[10px] font-black uppercase text-slate-400 mt-2">NEW ORGS</p></div>
              <div className="p-8 bg-slate-50 rounded border border-slate-100 text-center"><p className="text-4xl font-black">{importResults.updated}</p><p className="text-[10px] font-black uppercase text-slate-400 mt-2">UPDATED</p></div>
            </div>
            <Button onClick={() => navigate('/organizations')} className="px-16 py-5 shadow-2xl">Return to Directory</Button>
          </div>
        )}
      </div>
    </div>
  );
};